import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import CommonSelect from "../../../core/common/commonSelect";
import MultiSelectSection, { Option } from "../../../core/common/multiSelect";
import Table from "../../../core/common/dataTable/index";
import TooltipOption from "../../../core/common/tooltipOption";
import {
  createCourseStructure,
  getAllCourseStructures,
  getCourseStructureById,
  updateCourseStructure,
  deleteCourseStructure,
  getBreakdownsByClasses,
  CourseStructure,
  CourseSubCategory,
  CreateCourseStructureData,
  UpdateCourseStructureData,
  Breakdown,
} from "../../../service/course-structure";
import { getAllSystems } from "../../../service/systems";
import { getAllClasses } from "../../../service/classes";
import { getAllCourses } from "../../../service/courses";
import { getAllSections } from "../../../service/classes";
import { all_routes } from "../../router/all_routes";
import { DEFAULT_BRANCH_ID } from "../../../constant";
import { selectCurrentSession } from "../../../core/data/redux/slice/sessionsSlice";
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice";
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos";
import { getAllSessions } from "../../../service/sessions";

const CourseStructureManagement = () => {
  const routes = all_routes;
  const currentSession = useSelector(selectCurrentSession);
  const currentBranch = useSelector(selectCurrentBranch);
  const componentRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [courseStructures, setCourseStructures] = useState<CourseStructure[]>([]);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 10,
  });

  // Dropdown options
  const [sessions, setSessions] = useState<Option[]>([]);
  const [systems, setSystems] = useState<Option[]>([]);
  const [classes, setClasses] = useState<Option[]>([]);
  const [breakdowns, setBreakdowns] = useState<Option[]>([]);
  const [courses, setCourses] = useState<Option[]>([]);
  const [sections, setSections] = useState<Option[]>([]);
  const [breakdownsLoading, setBreakdownsLoading] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    session: "",
    branch: DEFAULT_BRANCH_ID,
    system: "",
    class: "",
    breakdown: "",
    course: "",
    section: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    session: "",
    branch: DEFAULT_BRANCH_ID,
    system: "",
    class: "",
    breakdown: "",
    course: "",
    section: "",
  });

  // Sort options
  const sortOptions = [
    { label: "Newest First", value: { sortBy: "createdAt", sortOrder: "desc" } },
    { label: "Oldest First", value: { sortBy: "createdAt", sortOrder: "asc" } },
    { label: "Total Marks (High to Low)", value: { sortBy: "totalMarks", sortOrder: "desc" } },
    { label: "Total Marks (Low to High)", value: { sortBy: "totalMarks", sortOrder: "asc" } },
  ];

  const [sortBy, setSortBy] = useState(sortOptions[0]);

  // Form data
  const [formData, setFormData] = useState<CreateCourseStructureData>({
    sessionId: "",
    branchId: DEFAULT_BRANCH_ID,
    systemId: "",
    classIds: [],
    courseId: "",
    breakdownId: "",
    sectionId: "",
    allowSum: false,
    totalMarks: 0,
    subCategories: [],
  });

  const [editFormData, setEditFormData] = useState<UpdateCourseStructureData & { sectionId: string }>({
    allowSum: false,
    totalMarks: 0,
    sectionId: "",
    subCategories: [],
  });

  const [selectedClassIds, setSelectedClassIds] = useState<Option[]>([]);
  const [selectedCourseStructureId, setSelectedCourseStructureId] = useState<string>("");
  const [selectedCourseStructure, setSelectedCourseStructure] = useState<CourseStructure | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const handlePrint = useReactToPrint({ contentRef: componentRef });

  // Fetch dropdown data
  const fetchDropdownData = async () => {
    try {
      // Fetch sessions
      const sessionsResponse = await getAllSessions();
      const sessionsData = Array.isArray(sessionsResponse)
        ? sessionsResponse
        : sessionsResponse?.sessions || sessionsResponse?.sessions || [];
      setSessions(
        sessionsData.map((s: any) => ({
          value: s._id,
          label: s.title,
        }))
      );

      // Fetch systems
      const systemsResponse = await getAllSystems();
      const systemsData = Array.isArray(systemsResponse)
        ? systemsResponse
        : systemsResponse?.data || systemsResponse?.systems || [];
      setSystems(
        systemsData.map((s: any) => ({
          value: s._id,
          label: s.name,
        }))
      );

      // Fetch classes
      const classesResponse = await getAllClasses({});
      const classesData = classesResponse.classes || [];
      setClasses(
        classesData.map((c: any) => ({
          value: c._id,
          label: c.name,
        }))
      );

      // Fetch courses
      const coursesResponse = await getAllCourses({});
      const coursesData = coursesResponse.data || [];
      setCourses(
        coursesData.map((c: any) => ({
          value: c._id,
          label: c.title || c.courseIdentifier,
        }))
      );

      // Fetch sections. This page manages course structures across any
      // campus/session (its own filter row), and sections vary by session
      // too — ?all=true here to show the full catalog rather than silently
      // scoping to whatever session happens to be globally selected.
      const sectionsResponse = await getAllSections({ all: "true" });
      const sectionsData = Array.isArray(sectionsResponse)
        ? sectionsResponse
        : sectionsResponse?.data || sectionsResponse?.sections || [];
      setSections(
        sectionsData.map((s: any) => ({
          value: s._id,
          label: s.name,
        }))
      );
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
      toast.error("Failed to load dropdown data");
    }
  };

  // Fetch breakdowns by selected classes
  const fetchBreakdownsByClasses = async (classIds: string[]) => {
    if (!classIds || classIds.length === 0) {
      setBreakdowns([]);
      return;
    }

    setBreakdownsLoading(true);
    try {
      const response = await getBreakdownsByClasses(classIds);
      const breakdownsData = response.data || [];
      setBreakdowns(
        breakdownsData.map((b: Breakdown) => ({
          value: b._id,
          label: b.title,
        }))
      );
    } catch (error) {
      console.error("Error fetching breakdowns:", error);
      toast.error("Failed to load breakdowns");
      setBreakdowns([]);
    } finally {
      setBreakdownsLoading(false);
    }
  };

  // Fetch course structures
  const fetchCourseStructures = async () => {
    setLoading(true);
    try {
      const response = await getAllCourseStructures({
        page: pagination.currentPage,
        limit: pagination.limit,
        sortBy: sortBy.value.sortBy,
        order: sortBy.value.sortOrder as "asc" | "desc",
        session: appliedFilters.session || currentSession?._id,
        branch: appliedFilters.branch,
        system: appliedFilters.system,
        class: appliedFilters.class,
        breakdown: appliedFilters.breakdown,
        course: appliedFilters.course,
        section: appliedFilters.section,
      });

      if (response.success) {
        const data = Array.isArray(response.data) ? response.data : [];
        setCourseStructures(data);
        setPagination(
          response.pagination || {
            currentPage: 1,
            totalPages: 1,
            total: 0,
            limit: 10,
          }
        );
      }
    } catch (error: any) {
      console.error("Error fetching course structures:", error);
      toast.error(error.response?.data?.error || "Failed to load course structures");
      setCourseStructures([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDropdownData();
  }, []);

  // Depends only on appliedFilters, not currentSession?._id directly — the
  // "Set default session" effect below is the single source of truth for
  // session changes. Depending on both caused a race: this effect fired
  // once immediately with the still-stale appliedFilters (same commit,
  // before that effect's setState took effect), and once more after
  // appliedFilters actually updated — if the first (stale) request resolved
  // later, it silently overwrote the correct results.
  useEffect(() => {
    fetchCourseStructures();
  }, [pagination.currentPage, appliedFilters, sortBy]);

  // Fetch breakdowns when classes are selected
  useEffect(() => {
    if (selectedClassIds.length > 0) {
      const classIds = selectedClassIds.map((opt) => opt.value);
      fetchBreakdownsByClasses(classIds);
    } else {
      setBreakdowns([]);
    }
  }, [selectedClassIds]);

  // Set default session for filters only (not form data)
  useEffect(() => {
    if (currentSession?._id && sessions.length > 0) {
      const defaultSession = sessions.find((s) => s.value === currentSession?._id);
      if (defaultSession) {
        setFilters((prev) => ({
          ...prev,
          session: currentSession?._id,
        }));
        setAppliedFilters((prev) => ({
          ...prev,
          session: currentSession?._id,
        }));
      }
    }
  }, [currentSession, sessions]);

  const handleSortSelect = (item: typeof sortBy) => {
    setSortBy(item);
  };

  const handleResetFilters = () => {
    setFilters({
      session: currentSession?._id || "",
      branch: DEFAULT_BRANCH_ID,
      system: "",
      class: "",
      breakdown: "",
      course: "",
      section: "",
    });
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show");
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      sessionId: "",
      branchId: DEFAULT_BRANCH_ID,
      systemId: "",
      classIds: [],
      courseId: "",
      breakdownId: "",
      sectionId: "",
      allowSum: false,
      totalMarks: 0,
      subCategories: [],
    });
    setSelectedClassIds([]);
    setBreakdowns([]);
    setFormErrors({});
  };

  // Validation
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.sessionId) errors.sessionId = "Session is required";
    if (!formData.branchId) errors.branchId = "Branch is required";
    if (!formData.systemId) errors.systemId = "System is required";
    if (!selectedClassIds || selectedClassIds.length === 0) {
      errors.classIds = "At least one class is required";
    }
    if (!formData.courseId) errors.courseId = "Course is required";
    if (!formData.breakdownId) errors.breakdownId = "Breakdown is required";
    if (!formData.sectionId) errors.sectionId = "Section is required";
    if (formData.totalMarks < 0) errors.totalMarks = "Total marks must be non-negative";

    if (formData.allowSum) {
      const subCategories = formData.subCategories || [];
      const sum = subCategories.reduce((s, sc) => s + (Number(sc.marks) || 0), 0);
      if (Math.abs(sum - Number(formData.totalMarks)) > 1e-6) {
        errors.subCategories = `Sum of sub-category marks (${sum}) must equal total marks (${formData.totalMarks})`;
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle create
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fix form errors");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: CreateCourseStructureData = {
        sessionId: formData.sessionId,
        branchId: formData.branchId,
        systemId: formData.systemId,
        classIds: selectedClassIds.map((opt) => opt.value),
        courseId: formData.courseId,
        breakdownId: formData.breakdownId,
        sectionId: formData.sectionId,
        allowSum: formData.allowSum,
        totalMarks: Number(formData.totalMarks),
        subCategories: formData.subCategories || [],
      };

      const response = await createCourseStructure(payload);

      if (response.success) {
        const isBulk = Array.isArray(response.data);
        if (isBulk) {
          const dataArray = response.data as CourseStructure[];
          toast.success(
            `Course structure(s) created for ${dataArray.length} class(es)`
          );
        } else {
          toast.success("Course structure created successfully");
        }

        resetForm();
        const closeButton = document.querySelector(
          '#add_course_structure [data-bs-dismiss="modal"]'
        ) as HTMLElement;
        if (closeButton) closeButton.click();

        fetchCourseStructures();
      }
    } catch (error: any) {
      console.error("Error creating course structure:", error);
      toast.error(
        error.response?.data?.error || error.response?.data?.message || "Failed to create course structure"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle update
  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCourseStructureId) return;

    setIsSubmitting(true);
    try {
      const payload: UpdateCourseStructureData = {
        allowSum: editFormData.allowSum,
        totalMarks: editFormData.totalMarks !== undefined ? Number(editFormData.totalMarks) : undefined,
        sectionId: editFormData.sectionId,
        subCategories: editFormData.subCategories,
      };

      const response = await updateCourseStructure(selectedCourseStructureId, payload);

      if (response.success) {
        toast.success("Course structure updated successfully");

        setEditFormData({
          allowSum: false,
          totalMarks: 0,
          sectionId: "",
          subCategories: [],
        });
        setSelectedCourseStructureId("");
        setSelectedCourseStructure(null);

        const closeButton = document.querySelector(
          '#edit_course_structure [data-bs-dismiss="modal"]'
        ) as HTMLElement;
        if (closeButton) closeButton.click();

        fetchCourseStructures();
      }
    } catch (error: any) {
      console.error("Error updating course structure:", error);
      toast.error(
        error.response?.data?.error || error.response?.data?.message || "Failed to update course structure"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!selectedCourseStructureId) return;

    setIsSubmitting(true);
    try {
      const response = await deleteCourseStructure(selectedCourseStructureId);

      if (response.success) {
        toast.success("Course structure deleted successfully");
        setSelectedCourseStructureId("");

        const closeButton = document.querySelector(
          '#delete_course_structure [data-bs-dismiss="modal"]'
        ) as HTMLElement;
        if (closeButton) closeButton.click();

        fetchCourseStructures();
      }
    } catch (error: any) {
      console.error("Error deleting course structure:", error);
      toast.error(
        error.response?.data?.error || error.response?.data?.message || "Failed to delete course structure"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Prepare edit
  const prepareEdit = async (courseStructure: CourseStructure) => {
    try {
      setSelectedCourseStructureId(courseStructure._id);
      setSelectedCourseStructure(courseStructure);

      // Fetch breakdowns for the class if needed
      if (courseStructure.class?._id) {
        await fetchBreakdownsByClasses([courseStructure.class._id]);
      }

      // Fetch full details
      const response = await getCourseStructureById(courseStructure._id);
      if (response.success && response.data) {
        const data = Array.isArray(response.data) ? response.data[0] : response.data;
        setEditFormData({
          allowSum: data.allowSum,
          totalMarks: data.totalMarks,
          sectionId: data.section._id,
          subCategories: data.subCategories || [],
        });
      }
    } catch (error) {
      console.error("Error loading course structure:", error);
      toast.error("Failed to load course structure details");
    }
  };

  // Prepare delete
  const prepareDelete = (courseStructureId: string) => {
    setSelectedCourseStructureId(courseStructureId);
  };

  // Add sub-category
  const addSubCategory = (isEdit = false) => {
    const newSubCategory: CourseSubCategory = {
      description: "",
      marks: 0,
    };

    if (isEdit) {
      setEditFormData((prev) => ({
        ...prev,
        subCategories: [...(prev.subCategories || []), newSubCategory],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        subCategories: [...(prev.subCategories || []), newSubCategory],
      }));
    }
  };

  // Remove sub-category
  const removeSubCategory = (index: number, isEdit = false) => {
    if (isEdit) {
      setEditFormData((prev) => {
        const updated = [...(prev.subCategories || [])];
        updated.splice(index, 1);
        return { ...prev, subCategories: updated };
      });
    } else {
      setFormData((prev) => {
        const updated = [...(prev.subCategories || [])];
        updated.splice(index, 1);
        return { ...prev, subCategories: updated };
      });
    }
  };

  // Update sub-category
  const updateSubCategory = (
    index: number,
    field: "description" | "marks",
    value: string | number,
    isEdit = false
  ) => {
    if (isEdit) {
      setEditFormData((prev) => {
        const updated = [...(prev.subCategories || [])];
        updated[index] = {
          ...updated[index],
          [field]: field === "marks" ? Number(value) : value,
        };
        return { ...prev, subCategories: updated };
      });
    } else {
      setFormData((prev) => {
        const updated = [...(prev.subCategories || [])];
        updated[index] = {
          ...updated[index],
          [field]: field === "marks" ? Number(value) : value,
        };
        return { ...prev, subCategories: updated };
      });
    }
  };

  // Calculate sub-categories sum
  const calculateSubCategoriesSum = (subCategories: CourseSubCategory[] = []): number => {
    return subCategories.reduce((sum, sc) => sum + (Number(sc.marks) || 0), 0);
  };

  // Export to Excel
  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      courseStructures.map((cs) => ({
        Session: cs.session?.title || "N/A",
        Branch: cs.branch?.name || "N/A",
        System: cs.system?.name || "N/A",
        Class: cs.class?.name || "N/A",
        Breakdown: cs.breakdown?.title || "N/A",
        Course: cs.course?.title || cs.course?.courseIdentifier || "N/A",
        Section: cs.section?.name || "N/A",
        "Total Marks": cs.totalMarks || 0,
        "Allow Sum": cs.allowSum ? "Yes" : "No",
        "Sub-Categories Count": cs.subCategories?.length || 0,
        "Created Date": new Date(cs.createdAt).toLocaleDateString(),
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Course Structures");
    XLSX.writeFile(wb, "course_structures.xlsx");
  };

  // Table columns
  const columns = [
    {
      title: "Session",
      dataIndex: "session",
      render: (_: any, record: CourseStructure) => record.session?.title || "N/A",
    },
    {
      title: "Class",
      dataIndex: "class",
      render: (_: any, record: CourseStructure) => record.class?.name || "N/A",
    },
    {
      title: "Breakdown",
      dataIndex: "breakdown",
      render: (_: any, record: CourseStructure) => record.breakdown?.title || "N/A",
    },
    {
      title: "Course",
      dataIndex: "course",
      render: (_: any, record: CourseStructure) =>
        record.course?.title || record.course?.courseIdentifier || "N/A",
    },
    {
      title: "Section",
      dataIndex: "section",
      render: (_: any, record: CourseStructure) => record.section?.name || "N/A",
    },
    {
      title: "Total Marks",
      dataIndex: "totalMarks",
      render: (marks: number) => marks || 0,
    },
    {
      title: "Allow Sum",
      dataIndex: "allowSum",
      render: (allowSum: boolean) => (allowSum ? "Yes" : "No"),
    },
    {
      title: "Sub-Categories",
      dataIndex: "subCategories",
      render: (_: any, record: CourseStructure) => record.subCategories?.length || 0,
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: CourseStructure) => (
        <div className="d-flex align-items-center">
          <div className="dropdown">
            <Link
              to="#"
              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ti ti-dots-vertical fs-14" />
            </Link>
            <ul className="dropdown-menu dropdown-menu-right p-3">
              <li>
                <Link
                  className="dropdown-item rounded-1"
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#edit_course_structure"
                  onClick={() => prepareEdit(record)}
                >
                  <i className="ti ti-edit-circle me-2" />
                  Edit
                </Link>
              </li>
              <li>
                <Link
                  className="dropdown-item rounded-1 text-danger"
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#delete_course_structure"
                  onClick={() => prepareDelete(record._id)}
                >
                  <i className="ti ti-trash me-2" />
                  Delete
                </Link>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
  ];

  const subCategoriesSum = calculateSubCategoriesSum(formData.subCategories);
  const editSubCategoriesSum = calculateSubCategoriesSum(editFormData.subCategories);

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Course Structure</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Academic</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Course Structure
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="d-flex align-items-center gap-2">
                <TooltipOption
                  onRefresh={fetchCourseStructures}
                  onExportPdf={handlePrint}
                  onExportExcel={handleExportExcel}
                />
                <button
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#add_course_structure"
                  onClick={resetForm}
                >
                  <i className="ti ti-plus me-2" />
                  Add Course Structure
                </button>
              </div>
            </div>
          </div>

          {/* Course Structures List */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Course Structures List</h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <i className="ti ti-filter me-2" />
                    Filter
                  </Link>
                  <div
                    className="dropdown-menu drop-width"
                    ref={dropdownMenuRef}
                  >
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 border-bottom pb-0">
                        <div className="row">
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">Session</label>
                              <CommonSelect
                                className="select"
                                options={sessions}
                                value={sessions.find((s) => s.value === (filters.session || currentSession?._id))}
                                onChange={(option) =>
                                  setFilters((prev) => ({ ...prev, session: option?.value || "" }))
                                }
                                placeholder="Select Session"
                              />
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">System</label>
                              <CommonSelect
                                className="select"
                                options={systems}
                                value={systems.find((s) => s.value === filters.system)}
                                onChange={(option) =>
                                  setFilters((prev) => ({ ...prev, system: option?.value || "" }))
                                }
                                placeholder="Select System"
                              />
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">Class</label>
                              <CommonSelect
                                className="select"
                                options={classes}
                                value={classes.find((c) => c.value === filters.class)}
                                onChange={(option) =>
                                  setFilters((prev) => ({ ...prev, class: option?.value || "" }))
                                }
                                placeholder="Select Class"
                              />
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">Breakdown</label>
                              <CommonSelect
                                className="select"
                                options={breakdowns}
                                value={breakdowns.find((b) => b.value === filters.breakdown)}
                                onChange={(option) =>
                                  setFilters((prev) => ({ ...prev, breakdown: option?.value || "" }))
                                }
                                placeholder="Select Breakdown"
                              />
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">Course</label>
                              <CommonSelect
                                className="select"
                                options={courses}
                                value={courses.find((c) => c.value === filters.course)}
                                onChange={(option) =>
                                  setFilters((prev) => ({ ...prev, course: option?.value || "" }))
                                }
                                placeholder="Select Course"
                              />
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">Section</label>
                              <CommonSelect
                                className="select"
                                options={sections}
                                value={sections.find((s) => s.value === filters.section)}
                                onChange={(option) =>
                                  setFilters((prev) => ({ ...prev, section: option?.value || "" }))
                                }
                                placeholder="Select Section"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <Link
                          to="#"
                          className="btn btn-light me-3"
                          onClick={(e) => {
                            e.preventDefault();
                            handleResetFilters();
                          }}
                        >
                          Reset
                        </Link>
                        <Link
                          to="#"
                          className="btn btn-primary"
                          onClick={(e) => {
                            e.preventDefault();
                            handleApplyFilters();
                          }}
                        >
                          Apply
                        </Link>
                      </div>
                    </form>
                  </div>
                </div>
                <div className="dropdown mb-3">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-sort-ascending-2 me-2" />
                    Sort by {sortBy.label}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    {sortOptions.map((item, index) => (
                      <li key={index}>
                        <div
                          className={`dropdown-item rounded-1 ${item.label === sortBy.label ? "active" : ""
                            }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSortSelect(item)}
                        >
                          {item.label}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className="card-body p-0 py-3 printable-content" ref={componentRef}>
              <div className="print-only-header">
                <div className="school-brand">
                  <img src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"} alt={campusBrandName(currentBranch)} />
                  <strong>{campusBrandName(currentBranch)}{campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}</strong>
                </div>
                <div className="report-title">Course Structure</div>
                <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
              </div>
              <Table
                columns={columns}
                dataSource={courseStructures}
                loading={loading}
                setSearchText={setSearchText}
                pagination={{
                  currentPage: pagination.currentPage,
                  limit: pagination.limit,
                  total: pagination.total,
                  onChange: (page, pageSize) => setPagination((prev) => ({ ...prev, currentPage: page, limit: pageSize || prev.limit })),
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Add Course Structure Modal */}
      <div className="modal fade" id="add_course_structure">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Add Course Structure</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={resetForm}
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Session <span className="text-danger">*</span>
                      </label>
                      <CommonSelect
                        options={sessions}
                        value={sessions.find((s) => s.value === formData.sessionId)}
                        onChange={(option) => {
                          setFormData((prev) => ({ ...prev, sessionId: option?.value || "" }));
                          if (formErrors.sessionId) {
                            setFormErrors((prev) => ({ ...prev, sessionId: "" }));
                          }
                        }}
                        placeholder="Select Session"
                      />
                      {formErrors.sessionId && (
                        <div className="invalid-feedback d-block">{formErrors.sessionId}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        System <span className="text-danger">*</span>
                      </label>
                      <CommonSelect
                        options={systems}
                        value={formData.systemId ? systems.find((s) => s.value === formData.systemId) || null : null}
                        onChange={(option) => {
                          setFormData((prev) => ({ ...prev, systemId: option?.value || "" }));
                          if (formErrors.systemId) {
                            setFormErrors((prev) => ({ ...prev, systemId: "" }));
                          }
                        }}
                        placeholder="Select System"
                      />
                      {formErrors.systemId && (
                        <div className="invalid-feedback d-block">{formErrors.systemId}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Classes <span className="text-danger">*</span>
                      </label>
                      <MultiSelectSection
                        options={classes}
                        value={selectedClassIds}
                        onChange={(selected) => {
                          setSelectedClassIds(selected);
                          setFormData((prev) => ({
                            ...prev,
                            classIds: selected.map((opt) => opt.value),
                          }));
                          if (formErrors.classIds) {
                            setFormErrors((prev) => ({ ...prev, classIds: "" }));
                          }
                        }}
                        placeholder="Select one or more classes..."
                      />
                      {formErrors.classIds && (
                        <div className="invalid-feedback d-block">{formErrors.classIds}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Breakdown <span className="text-danger">*</span>
                      </label>
                      <CommonSelect
                        options={breakdowns}
                        value={breakdowns.find((b) => b.value === formData.breakdownId)}
                        onChange={(option) => {
                          setFormData((prev) => ({ ...prev, breakdownId: option?.value || "" }));
                          if (formErrors.breakdownId) {
                            setFormErrors((prev) => ({ ...prev, breakdownId: "" }));
                          }
                        }}
                        placeholder={breakdownsLoading ? "Loading..." : "Select Breakdown"}
                        isDisabled={breakdownsLoading || selectedClassIds.length === 0}
                      />
                      {formErrors.breakdownId && (
                        <div className="invalid-feedback d-block">{formErrors.breakdownId}</div>
                      )}
                      {breakdownsLoading && (
                        <div className="form-text">Loading breakdowns...</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Course <span className="text-danger">*</span>
                      </label>
                      <CommonSelect
                        options={courses}
                        value={formData.courseId ? courses.find((c) => c.value === formData.courseId) || null : null}
                        onChange={(option) => {
                          setFormData((prev) => ({ ...prev, courseId: option?.value || "" }));
                          if (formErrors.courseId) {
                            setFormErrors((prev) => ({ ...prev, courseId: "" }));
                          }
                        }}
                        placeholder="Select Course"
                      />
                      {formErrors.courseId && (
                        <div className="invalid-feedback d-block">{formErrors.courseId}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Section <span className="text-danger">*</span>
                      </label>
                      <CommonSelect
                        options={sections}
                        value={sections.find((s) => s.value === formData.sectionId)}
                        onChange={(option) => {
                          setFormData((prev) => ({ ...prev, sectionId: option?.value || "" }));
                          if (formErrors.sectionId) {
                            setFormErrors((prev) => ({ ...prev, sectionId: "" }));
                          }
                        }}
                        placeholder="Select Section"
                      />
                      {formErrors.sectionId && (
                        <div className="invalid-feedback d-block">{formErrors.sectionId}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Total Marks <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        value={formData.totalMarks}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          setFormData((prev) => ({ ...prev, totalMarks: value >= 0 ? value : 0 }));
                          if (formErrors.totalMarks) {
                            setFormErrors((prev) => ({ ...prev, totalMarks: "" }));
                          }
                        }}
                        min="0"
                        required
                      />
                      {formErrors.totalMarks && (
                        <div className="invalid-feedback d-block">{formErrors.totalMarks}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Allow Sum</label>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.allowSum}
                          onChange={(e) => {
                            setFormData((prev) => ({ ...prev, allowSum: e.target.checked }));
                            if (formErrors.subCategories) {
                              setFormErrors((prev) => ({ ...prev, subCategories: "" }));
                            }
                          }}
                        />
                        <label className="form-check-label">
                          Enable sum validation for sub-categories
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="form-label mb-0">Sub-Categories</label>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() => addSubCategory(false)}
                        >
                          <i className="ti ti-plus me-1" />
                          Add Sub-Category
                        </button>
                      </div>
                      {formData.subCategories && formData.subCategories.length > 0 && (
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead>
                              <tr>
                                <th>Description</th>
                                <th>Marks</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {formData.subCategories.map((subCat, index) => (
                                <tr key={index}>
                                  <td>
                                    <input
                                      type="text"
                                      className="form-control"
                                      value={subCat.description}
                                      onChange={(e) =>
                                        updateSubCategory(index, "description", e.target.value, false)
                                      }
                                      placeholder="Enter description"
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      className="form-control"
                                      value={subCat.marks}
                                      onChange={(e) =>
                                        updateSubCategory(
                                          index,
                                          "marks",
                                          Number(e.target.value) || 0,
                                          false
                                        )
                                      }
                                      min="0"
                                      placeholder="Enter marks"
                                    />
                                  </td>
                                  <td>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-danger"
                                      onClick={() => removeSubCategory(index, false)}
                                    >
                                      <i className="ti ti-trash" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr>
                                <th>Total</th>
                                <th>{subCategoriesSum}</th>
                                <th></th>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                      {formData.allowSum && formData.subCategories && formData.subCategories.length > 0 && (
                        <div className="mt-2">
                          {Math.abs(subCategoriesSum - Number(formData.totalMarks)) > 1e-6 ? (
                            <div className="text-danger">
                              Sum of sub-category marks ({subCategoriesSum}) must equal total marks (
                              {formData.totalMarks})
                            </div>
                          ) : (
                            <div className="text-success">
                              ✓ Sub-category marks sum matches total marks
                            </div>
                          )}
                        </div>
                      )}
                      {formErrors.subCategories && (
                        <div className="invalid-feedback d-block">{formErrors.subCategories}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light me-2" data-bs-dismiss="modal" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? "Creating..." : "Create Course Structure"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Edit Course Structure Modal */}
      <div className="modal fade" id="edit_course_structure">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Edit Course Structure</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={() => {
                  setSelectedCourseStructure(null);
                  setEditFormData({
                    allowSum: false,
                    totalMarks: 0,
                    sectionId: "",
                    subCategories: [],
                  });
                }}
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleUpdate}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Session</label>
                      <CommonSelect
                        options={sessions}
                        value={sessions.find((s) => s.value === (selectedCourseStructure?.session?._id || ""))}
                        onChange={() => {}}
                        placeholder="Select Session"
                        isDisabled={true}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">System</label>
                      <CommonSelect
                        options={systems}
                        value={systems.find((s) => s.value === (selectedCourseStructure?.system?._id || ""))}
                        onChange={() => {}}
                        placeholder="Select System"
                        isDisabled={true}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Class</label>
                      <CommonSelect
                        options={classes}
                        value={classes.find((c) => c.value === (selectedCourseStructure?.class?._id || ""))}
                        onChange={() => {}}
                        placeholder="Select Class"
                        isDisabled={true}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Breakdown</label>
                      <CommonSelect
                        options={breakdowns}
                        value={breakdowns.find((b) => b.value === (selectedCourseStructure?.breakdown?._id || ""))}
                        onChange={() => {}}
                        placeholder="Select Breakdown"
                        isDisabled={true}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Course</label>
                      <CommonSelect
                        options={courses}
                        value={courses.find((c) => c.value === (selectedCourseStructure?.course?._id || ""))}
                        onChange={() => {}}
                        placeholder="Select Course"
                        isDisabled={true}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Section</label>
                      <CommonSelect
                        options={sections}
                        value={sections.find((s) => s.value === editFormData.sectionId)}
                        onChange={(option) => {
                          setEditFormData((prev) => ({
                            ...prev,
                            sectionId: option?.value || "",
                          }));
                        }}
                        placeholder="Select Section"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Total Marks</label>
                      <input
                        type="number"
                        className="form-control"
                        value={editFormData.totalMarks || 0}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          setEditFormData((prev) => ({
                            ...prev,
                            totalMarks: value >= 0 ? value : 0,
                          }));
                        }}
                        min="0"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Allow Sum</label>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={editFormData.allowSum || false}
                          onChange={(e) => {
                            setEditFormData((prev) => ({ ...prev, allowSum: e.target.checked }));
                          }}
                        />
                        <label className="form-check-label">
                          Enable sum validation for sub-categories
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="form-label mb-0">Sub-Categories</label>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() => addSubCategory(true)}
                        >
                          <i className="ti ti-plus me-1" />
                          Add Sub-Category
                        </button>
                      </div>
                      {editFormData.subCategories && editFormData.subCategories.length > 0 && (
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead>
                              <tr>
                                <th>Description</th>
                                <th>Marks</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {editFormData.subCategories.map((subCat, index) => (
                                <tr key={index}>
                                  <td>
                                    <input
                                      type="text"
                                      className="form-control"
                                      value={subCat.description}
                                      onChange={(e) =>
                                        updateSubCategory(index, "description", e.target.value, true)
                                      }
                                      placeholder="Enter description"
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      className="form-control"
                                      value={subCat.marks}
                                      onChange={(e) =>
                                        updateSubCategory(
                                          index,
                                          "marks",
                                          Number(e.target.value) || 0,
                                          true
                                        )
                                      }
                                      min="0"
                                      placeholder="Enter marks"
                                    />
                                  </td>
                                  <td>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-danger"
                                      onClick={() => removeSubCategory(index, true)}
                                    >
                                      <i className="ti ti-trash" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr>
                                <th>Total</th>
                                <th>{editSubCategoriesSum}</th>
                                <th></th>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                      {editFormData.allowSum &&
                        editFormData.subCategories &&
                        editFormData.subCategories.length > 0 && (
                          <div className="mt-2">
                            {Math.abs(editSubCategoriesSum - Number(editFormData.totalMarks || 0)) >
                            1e-6 ? (
                              <div className="text-danger">
                                Sum of sub-category marks ({editSubCategoriesSum}) must equal total
                                marks ({editFormData.totalMarks || 0})
                              </div>
                            ) : (
                              <div className="text-success">
                                ✓ Sub-category marks sum matches total marks
                              </div>
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  onClick={() => {
                    setSelectedCourseStructure(null);
                    setEditFormData({
                      allowSum: false,
                      totalMarks: 0,
                      sectionId: "",
                      subCategories: [],
                    });
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <div className="modal fade" id="delete_course_structure">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <form>
              <div className="modal-body text-center">
                <span className="delete-icon">
                  <i className="ti ti-trash-x" />
                </span>
                <h4>Confirm Deletion</h4>
                <p>You want to delete this course structure? This action cannot be undone.</p>
                <div className="d-flex justify-content-center">
                  <button
                    type="button"
                    className="btn btn-light me-3"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDelete}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseStructureManagement;
